import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';

/**
 * Custom hook for checking ANAF OAuth authentication status
 *
 * Provides real-time status of the current user's ANAF authentication,
 * including token validity, certificate holder info, and expiration status.
 *
 * @param {number} refreshInterval - Auto-refresh interval in milliseconds (default: 5 minutes)
 * @returns {Object} ANAF status data and refresh function
 */
export function useAnafStatus(refreshInterval = 300000) {
  const [status, setStatus] = useState({
    loading: true,
    error: null,
    hasToken: false,
    isActive: false,
    isExpired: false,
    isExpiringSoon: false, // < 7 days
    certificateHolder: null,
    certificateRole: null,
    daysUntilExpiry: null,
    lastChecked: null
  });

  const checkStatus = useCallback(async () => {
    try {
      setStatus(prev => ({ ...prev, loading: true, error: null }));

      const res = await apiFetch('/api/anaf/oauth/status');

      if (!res.ok) {
        if (res.status === 404) {
          // No token found - user hasn't authenticated yet
          setStatus({
            loading: false,
            error: null,
            hasToken: false,
            isActive: false,
            isExpired: false,
            isExpiringSoon: false,
            certificateHolder: null,
            certificateRole: null,
            daysUntilExpiry: null,
            lastChecked: new Date()
          });
          return;
        }

        throw new Error('Eroare la verificarea statusului ANAF');
      }

      const data = await res.json();

      setStatus({
        loading: false,
        error: null,
        hasToken: data.has_token || false,
        isActive: data.is_active || false,
        isExpired: data.is_expired || data.is_refresh_expired || false,
        isExpiringSoon: (data.days_until_expiry !== null && data.days_until_expiry < 7),
        certificateHolder: data.certificate_holder || null,
        certificateRole: data.certificate_role || null,
        daysUntilExpiry: data.days_until_expiry !== null ? data.days_until_expiry : null,
        lastChecked: new Date()
      });

    } catch (err) {
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Eroare necunoscută',
        lastChecked: new Date()
      }));
    }
  }, []);

  // Check status on mount
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Auto-refresh at specified interval
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(checkStatus, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [checkStatus, refreshInterval]);

  // Listen for OAuth callback success
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'anaf_oauth_success') {
        // Another tab completed OAuth flow
        checkStatus();
        localStorage.removeItem('anaf_oauth_success');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkStatus]);

  return {
    ...status,
    refresh: checkStatus
  };
}
